import { astraTwMerge } from '~/astra'
import { ButtonTag } from '~/components'
import { CrumbItem } from '~/components/BreadcrumbsHeader'
import { IdDate, PRACTITIONER_CELL_LOCATION_INCLUDE, useLocations } from '~/storm'
import { getScheduleStatusesGap } from '~/utils/utils'

import { CellErrorHoverCard, CellErrorItem, getDateBreadcrumb } from '../../../TablesLayout/shared/CellErrorHoverCard'
import { usePractitionerCellErrors } from '../../../TablesLayout/shared/useCellErrors'
import { PreviewCellView, PreviewPractitioner } from '../Apply/types'

const locationColor = '#E6E9E8'

type PreviewTagProps = {
    label: string
    errors: CellErrorItem[]
    breadcrumbs: CrumbItem[]
    color: string
}

const PreviewTag = ({ label, errors, breadcrumbs, color }: PreviewTagProps) => {
    if (errors.length > 0) {
        return (
            <CellErrorHoverCard breadcrumbs={breadcrumbs} triggerInfo={{ shortName: label }} errors={errors} showErrorSources>
                <ButtonTag colorType={'room_conflict'} fillType="all" label={label} />
            </CellErrorHoverCard>
        )
    }
    return <ButtonTag colorType={color} fillType="all" label={label} />
}

type Props = {
    cell: PreviewCellView
    practitioner: PreviewPractitioner
    date: IdDate
}

export const PreviewCell = ({ cell, practitioner, date }: Props) => {
    const cellLocations = useLocations({
        where: { room_code: { in: cell.locations.map(location => location.roomCode) } },
        include: PRACTITIONER_CELL_LOCATION_INCLUDE,
    })
    const cellErrors = usePractitionerCellErrors({ practitioner_id: practitioner.id, scheduled_date: date.id }, cellLocations, cell.statuses)
    const breadcrumbs = [getDateBreadcrumb(date.id), { label: practitioner.short_name }]

    return (
        <div className={astraTwMerge('cell-wrapper grid h-full grid-cols-1 grid-rows-2 gap-y-1 p-1')}>
            <div className={astraTwMerge('cell flex items-stretch gap-x-0.5', getScheduleStatusesGap(cell.statuses.length))}>
                {cell.statuses.map(status => (
                    <PreviewTag
                        key={status.code}
                        label={status.label}
                        color={status.definition.color}
                        errors={cellErrors.errorsByStatus.get(status.label) ?? []}
                        breadcrumbs={breadcrumbs}
                    />
                ))}
            </div>
            <div className={astraTwMerge('flex items-stretch gap-x-0.5', cell.locations.length > 3 ? '-space-x-5' : '')}>
                {cell.locations.map(location => (
                    <PreviewTag
                        key={location.roomCode}
                        label={location.roomCode}
                        color={locationColor}
                        errors={cellErrors.errorsByLocation.get(location.roomCode) ?? []}
                        breadcrumbs={breadcrumbs}
                    />
                ))}
            </div>
        </div>
    )
}
